import {
  dfpSourceDocumentResponse,
  DfpStatementKind,
} from '@interfaces/dfp/response/dfpSourceDocumentsResponse';

/**
 * Textos da Análise Econômica que saíram do `dfp.component.ts` (07/10/2026) para o componente ficar abaixo do
 * teto de ~1.000 linhas do `AGENTS.md` antes de ganhar o aviso de documentos em processamento. Só texto: quem
 * mostra o toast ou a confirmação continua sendo o componente.
 */

/** Rótulo do demonstrativo para o texto da confirmação de remoção. Espelha o do painel de documentos de origem. */
const SOURCE_KIND_LABELS: Record<DfpStatementKind, string> = {
  [DfpStatementKind.IncomeStatement]: 'DRE',
  [DfpStatementKind.BalanceSheet]: 'Balanço',
  [DfpStatementKind.RevenueStatement]: 'Faturamento',
};

/**
 * Descrição da confirmação de "remover documento de origem" (card #2932). Diz **o que sai da tela**
 * (demonstrativo + anos da cobertura), porque quem confirma está olhando uma tabela de números, não uma lista de
 * arquivos.
 */
export function describeSourceRemoval(document: dfpSourceDocumentResponse): string {
  const years = [...new Set(document.coverage.map(item => item.year))].sort((a, b) => a - b);
  return years.length
    ? `Os valores de ${SOURCE_KIND_LABELS[document.kind]} de ${years.join(', ')} saem da Análise ` +
        'Econômica. O arquivo continua guardado e pode ser enviado de novo pela Padronização de Documentos.'
    : 'Este documento não alimenta nenhuma coluna hoje. O arquivo continua guardado.';
}

/**
 * O `/dfp/status` recusou porque a empresa não tem Balanço/DRE enviados (validação do campo `companyId`, 422).
 * Só ESSA recusa é "sem documentos" e vai para o estado vazio. O backend manda esses itens sem `key` e com a
 * mensagem localizada, então o discriminador é o campo (`companyId`) com `key` vazia, nunca o texto. 403,
 * outros 422, `key: "Exception"`, rede e 5xx devolvem `false` e vão para o cartão de erro com a mensagem
 * do backend.
 */
export function isMissingDocumentsRefusal(error: unknown): boolean {
  const errors =
    (error as { errors?: { key?: string | null; field?: string | null }[] })?.errors ?? [];
  return (
    errors.length > 0 &&
    errors.every(item => !item.key && (item.field ?? '').toLowerCase() === 'companyid')
  );
}

/** Texto que só um dev entende: inglês de exceção/EF, rastro de pilha ou `key: "Exception"`. */
const TECHNICAL_TEXT = /\b(the|an error|cannot|exception|entity|inner|null|column|table|failed)\b/i;

/**
 * Mensagem do backend que serve para mostrar ao usuário: negócio e em português. Descarta `key: "Exception"`,
 * item com rastro de pilha e texto técnico (inglês, EF, SQL). Devolve `null` quando nada presta: aí a tela usa o
 * texto padrão em pt-BR, que diz o que fazer. É a regra única do cartão de erro (DFP e Modelagem), da simulação
 * e do salvar da modal de cenários.
 */
export function userFacingBackendMessage(error: unknown): string | null {
  const errors =
    (error as { errors?: { key?: string | null; message?: string; stackTrace?: unknown }[] })
      ?.errors ?? [];
  const item = errors.find(
    candidate =>
      !!candidate.message &&
      candidate.key !== 'Exception' &&
      !(Array.isArray(candidate.stackTrace) && candidate.stackTrace.length > 0) &&
      !TECHNICAL_TEXT.test(candidate.message)
  );
  return item?.message ?? null;
}
